<script lang="ts" setup>
const props = defineProps({
  loading: { type: Boolean, default: false },
  message: { type: String, default: '' }
})

const { stopScroll, continueScroll } = useScroll()

const checkScroll = () => {
  if (props.loading) stopScroll()
  else if (!props.loading) continueScroll()
}

onUpdated(() => checkScroll())
onMounted(() => checkScroll())
</script>

<template>
  <div v-if="loading" id="form-loading-screen" :class="`form-screen-${loading ? 'show' : 'hide'}`">
    <div id="form-loading-screen-bg">
      <div id="form-loading-screen-loading">
        <div id="form-loading-screen-loading-purple" class="form-loading-screen-circle"/>
        <div id="form-loading-screen-loading-blue" class="form-loading-screen-circle"/>
        <div id="form-loading-screen-loading-green" class="form-loading-screen-circle"/>
        <div id="form-loading-screen-loading-yellow" class="form-loading-screen-circle"/>
      </div>

      <div v-if="message" id="form-loading-screen-message">
        <p>
          {{ message }}
        </p>
        <div class="dots">
          <div
            v-for="i in 3"
            :key="`loading-screen-dot-${i}`"
            class="dot"
            :style="{ animationDelay: `${i * 0.5}s` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$animTime: 2s;
$blur: 15px;

#form-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;

  &-bg {
    min-height: 100dvh;
    height: 100dvh;
    min-width: 100dvw;
    width: 100dvw;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 40px;
    overflow: hidden;
    backdrop-filter: blur($blur);
    -webkit-backdrop-filter: blur($blur);
    animation: appear 0.3s ease-out;
    background-color: rgba(255, 255, 255, 0.6);
  }

  &-loading {
    $loadingSize: 130px;
    height: $loadingSize;
    width: $loadingSize;
    position: relative;
    animation: rotate $animTime ease-in-out infinite;
    display: flex;
    flex-wrap: wrap;

    .form-loading-screen-circle {
      height: 50%;
      width: 50%;
      border-radius: 50%;
      opacity: 0.7;
    }

    &-purple {
      background: var(--purple);
      animation: shrinkTopLeft $animTime ease-in-out infinite;
    }

    &-blue {
      background: var(--blue);
      animation: shrinkTopRight $animTime ease-in-out infinite;
    }

    &-green {
      background: var(--green);
      animation: shrinkBottomLeft $animTime ease-in-out infinite;
    }

    &-yellow {
      background: var(--yellow);
      animation: shrinkBottomRight $animTime ease-in-out infinite;
    }
  }

  &-message {
    max-width: 900px;
    text-align: center;

    p {
      display: inline;
      padding-right: 6px;
    }

    .dots {
      display: inline-flex;
      gap: 3px;
      padding-top: 5px;
      overflow: hidden;

      .dot {
        $dotSize: 3.5px;
        height: $dotSize;
        width: $dotSize;
        background-color: var(--black);
        border-radius: 50%;
        animation: dot 1.5s ease-out infinite;
        flex-shrink: 0;
        opacity: 0;
      }
    }
  }
}

.form-screen {
  &-show {
    height: 100dvh;
  }

  &-hide {
    border: 1px solid red;
    overflow: hidden;
    height: 0;
    display: none;
  }
}

@keyframes appear {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0);
  }
  50% {
    transform: rotate(360deg);
  }
  100% {
    transform: rotate(0);
  }
}

@keyframes shrinkTopLeft {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(0.5) translateX(200%) translateY(200%);
  }
  100% {
    transform: scale(0.9);
  }
}

@keyframes shrinkTopRight {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(0.5) translateX(-200%) translateY(200%);
  }
  100% {
    transform: scale(0.9);
  }
}

@keyframes shrinkBottomLeft {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(0.5) translateX(200%) translateY(-200%);
  }
  100% {
    transform: scale(0.9);
  }
}

@keyframes shrinkBottomRight {
  0% {
    transform: scale(0.9);
  }
  50% {
    transform: scale(0.5) translateX(-200%) translateY(-200%);
  }
  100% {
    transform: scale(0.9);
  }
}

@keyframes dot {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
